// WHICH PUBLISHED RATE IS IN FORCE ON A DATE -- the screen's reading of the
// FX schedule. PURE, like poLineMatch and inventoryRows, so it is tested on
// data rather than inferred from a screen.
//
// ⚠️ IT MIRRORS THE BACKEND'S RULE AND ADDS NOTHING TO IT. _fx_rate_domain on
// anixify.quote.line says a row is in force on a date when
//
//     effective_from is empty OR effective_from <= date
//   AND effective_to is empty OR effective_to >= date
//
// among ACTIVE rows, and _fx_rate_in_force refuses when that is not EXACTLY
// ONE row. This does the same and answers null in both refusing cases -- no
// row, or more than one. It must never pick "the newest" of two: that is the
// exact choice the backend's own docstring calls dangerous.
//
// ⚠️ IT DOES NOT PRICE ANYTHING. It reads a published figure off a row, for
// two DISPLAY purposes only: the typo guard when an Admin publishes a rate,
// and the amber note telling a person their draft was priced at a different
// rate. The number the engine multiplies by is still resolved by the backend,
// on save, and nothing here reaches a payload.

import type { FxRate } from "@/types";

/** The single active rate in force for `currency` on `onDate` (ISO), or null
 *  when there is none OR more than one -- both are "the schedule cannot
 *  answer", and the second must never be resolved by choosing. */
export function rateInForce(
  rates: FxRate[] | null | undefined,
  currency: string | null | undefined,
  onDate: string,
): number | null {
  const code = (currency ?? "").trim().toUpperCase();
  if (!code) return null;
  const matching = (rates ?? []).filter((r) =>
    r.active
    && (r.currency ?? "").toUpperCase() === code
    && (!r.effectiveFrom || r.effectiveFrom <= onDate)
    && (!r.effectiveTo || r.effectiveTo >= onDate),
  );
  return matching.length === 1 ? matching[0].rate : null;
}

/** How far `candidate` is from `reference`, as a percentage of the
 *  reference, always positive. null when there is no reference to compare
 *  against -- a first rate for a currency has nothing to be a typo OF. */
export function percentDifferent(
  candidate: number,
  reference: number | null | undefined,
): number | null {
  if (typeof reference !== "number" || !(reference > 0)) return null;
  return (Math.abs(candidate - reference) / reference) * 100;
}

/** The typo guard's threshold. Owner decision, 2026-09-22: a published rate
 *  more than 10% away from the current one is worth a second look. A
 *  WARNING, never a block -- currencies do move that far. */
export const TYPO_GUARD_PCT = 10;

/** Today as the backend sees it, in ISO form. Local date, not UTC: the
 *  backend's context_today is the company's day, and at 00:30 IST a UTC date
 *  would still be yesterday. */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** What the draft note says, or null when there is nothing to say.
 *
 *  ⚠️ OWNER DECISION, 2026-09-22 -- OPTION (i) PLUS A VISIBLE NOTICE. A saved
 *  draft KEEPS the rate it was priced at: its line re-sends that rate on every
 *  save, and the backend honours a sent rate as a statement ("EXPLICIT WINS").
 *  That is deliberate, because the app cannot tell a rate the SCHEDULE
 *  resolved from one a PERSON typed -- a forward contract looks exactly like
 *  a resolved rate on the wire. So nothing is changed on its own; this only
 *  NOTICES, and the person decides.
 *
 *  The set-marker that would let schedule-resolved rates re-resolve by
 *  themselves is the proper long-term fix, and it is deferred (HANDOFF).
 *
 *  Returns null when:
 *   - the quote is not on the import path, or names no supplier currency --
 *     there is no schedule to compare against;
 *   - the schedule cannot answer for today (no row, or more than one) --
 *     rateInForce refuses to choose, so this refuses to claim;
 *   - no line holds a rate that differs from today's.
 *
 *  A line with NO rate is not "stale": it will resolve from the schedule on
 *  its next save anyway, which is exactly where the button leaves it. */
export function staleRateNotice(
  rows: Array<{ lineNo: number; conversionRate?: number; rateSource?: "schedule" | "stated" }>,
  rates: FxRate[] | null | undefined,
  supplierCurrency: string | null | undefined,
  pricingMode: string,
  onDate: string,
): { lineNos: number[]; pricedAt: number[]; todayRate: number } | null {
  if (pricingMode !== "import" || !supplierCurrency) return null;
  const todayRate = rateInForce(rates, supplierCurrency, onDate);
  if (todayRate === null) return null;

  const stale = rows.filter((r) =>
    // A "schedule" line is never stale: it follows the schedule on its next
    // save by itself (fx-rate-source-marker). Only a STATED or a LEGACY line
    // can hold a rate nobody will move without a press.
    r.rateSource !== "schedule"
    && typeof r.conversionRate === "number" && r.conversionRate !== todayRate);
  if (stale.length === 0) return null;

  const pricedAt = Array.from(new Set(stale.map((r) => r.conversionRate as number)))
    .sort((a, b) => a - b);
  return { lineNos: stale.map((r) => r.lineNo), pricedAt, todayRate };
}

/** The note's one sentence, said once so the screen and the tests agree. */
export function staleRateText(notice: { pricedAt: number[]; todayRate: number }): string {
  return `Priced at ${notice.pricedAt.join(", ")} — today's rate is ${notice.todayRate}.`;
}
